<template>
  <PanelItem :index="index" :field="field">
    <template #value>
      <div class="mp-chart">
        <div class="mp-chart__toolbar">
          <div class="mp-chart__legend">
            <span
              class="mp-chart__swatch mp-chart__swatch--entry"
              :style="{ background: entryColor }"
            />
            {{ field.side_label }} · {{ (field.labels && field.labels.entry) || 'Entry' }}
            <span
              class="mp-chart__swatch mp-chart__swatch--prior"
              :style="{ background: priorColor }"
            />
            {{ (field.labels && field.labels.prior) || 'Prior entries' }}
            <span class="mp-chart__swatch mp-chart__swatch--tp" />
            TP
            <span class="mp-chart__swatch mp-chart__swatch--sl" />
            SL
          </div>
          <div class="mp-chart__intervals">
            <button
              type="button"
              class="mp-chart__tf mp-chart__tf--ruler"
              :class="{ 'mp-chart__tf--active': rulerActive }"
              :title="(field.labels && field.labels.ruler_hint) || 'Measure'"
              @click="toggleRuler"
            >
              📏 {{ (field.labels && field.labels.ruler) || 'Measure' }}
            </button>
            <button
              v-for="tf in intervals"
              :key="tf"
              type="button"
              class="mp-chart__tf"
              :class="{ 'mp-chart__tf--active': interval === tf }"
              @click="interval = tf"
            >
              {{ tf }}
            </button>
          </div>
        </div>

        <div
          ref="wrapper"
          class="mp-chart__stack"
          :class="{ 'mp-chart__stack--ruler': rulerActive }"
          :style="{ height: `${height}px` }"
        >
          <div v-if="candleLoading" class="mp-chart__status">Loading candles…</div>
          <div v-else-if="candleError" class="mp-chart__status mp-chart__status--error">
            {{ candleError }}
          </div>
          <div ref="container" class="mp-chart__pane" />

          <template v-if="rulerBox">
            <div
              class="mp-chart__ruler-box"
              :class="rulerBox.up ? 'mp-chart__ruler-box--up' : 'mp-chart__ruler-box--down'"
              :style="{
                left: `${rulerBox.left}px`,
                top: `${rulerBox.top}px`,
                width: `${rulerBox.width}px`,
                height: `${rulerBox.height}px`,
              }"
            />
            <div
              class="mp-chart__ruler-label"
              :class="rulerBox.up ? 'mp-chart__ruler-label--up' : 'mp-chart__ruler-label--down'"
              :style="{ left: `${rulerBox.labelLeft}px`, top: `${rulerBox.labelTop}px` }"
            >
              <span>{{ rulerBox.moveLabel }} · {{ rulerBox.pctLabel }}</span>
              <span v-if="rulerBox.roeLabel">{{ leverage }}x → {{ rulerBox.roeLabel }}</span>
              <span>{{ rulerBox.priceLabel }}</span>
              <span>{{ rulerBox.timeLabel }}</span>
            </div>
          </template>

          <div
            v-for="label in priorLabels"
            :key="label.key"
            class="mp-chart__prior"
            :style="{ top: `${label.top}px`, borderColor: priorColor }"
          >
            {{ label.text }}
          </div>

          <div
            v-if="pnl"
            class="mp-chart__pnl"
            :class="pnl.up ? 'mp-chart__pnl--up' : 'mp-chart__pnl--down'"
            :style="{ top: `${pnl.top}px` }"
          >
            <span class="mp-chart__pnl-lev">{{ leverage }}x</span>
            <span class="mp-chart__pnl-roe">{{ pnl.roeLabel }}</span>
            <span class="mp-chart__pnl-move">{{ pnl.moveLabel }}</span>
          </div>
        </div>
      </div>
    </template>
  </PanelItem>
</template>

<script>
import { createChart, ColorType, LineStyle } from 'lightweight-charts'
import { tehranChartClockOptions } from '@modules/heatmap/templates/js/composables/chartTime.js'

const INTERVALS = ['1m', '5m', '15m', '1h', '4h']

// Vertical room one prior-entry tag needs before it starts covering the next.
const LABEL_GAP = 20

const COLORS = {
  long: '#22c55e',
  short: '#ef4444',
  priorLong: 'rgba(34, 197, 94, 0.28)',
  priorShort: 'rgba(248, 113, 113, 0.22)',
  tp: '#3b82f6',
  sl: '#d4d4d8',
}

export default {
  props: ['index', 'resource', 'resourceName', 'resourceId', 'field'],

  data() {
    return {
      intervals: INTERVALS,
      interval: this.field.interval || '1m',
      height: this.field.height || 520,
      candleLoading: false,
      candleError: '',
      lastClose: 0,
      entryY: null,
      priorY: [],
      // Measure ("ruler") tool. Anchors are kept as logical {price, time} and
      // re-projected to pixels every frame so the box stays pinned on pan/zoom.
      rulerActive: false,
      rulerAnchor: null,
      rulerEnd: null,
      rulerLocked: false,
      rulerAnchorPx: null,
      rulerEndPx: null,
    }
  },

  computed: {
    isLong() {
      return String(this.field.side || '').toLowerCase() === 'long'
    },

    leverage() {
      return Number(this.field.leverage) || 0
    },

    /**
     * Live leverage-adjusted return on the open position, anchored to the
     * entry line. Only a point that is still open has money on the table —
     * a watching point was never filled and a closed one is settled, so
     * multiplying today's price against its entry would be fiction.
     */
    pnl() {
      if (String(this.field.status || '').toLowerCase() !== 'opened') return null

      const entry = Number(this.field.entry)
      const last = Number(this.lastClose)
      if (!(entry > 0) || !(last > 0) || !(this.leverage > 0)) return null
      if (this.entryY === null) return null

      const move = ((last - entry) / entry) * 100 * (this.isLong ? 1 : -1)
      const roe = move * this.leverage
      const abs = Math.abs(roe)

      return {
        up: roe >= 0,
        top: this.entryY,
        roeLabel: `${roe >= 0 ? '+' : '-'}${abs.toFixed(abs >= 100 ? 0 : 2)}%`,
        moveLabel: `${move >= 0 ? '+' : '-'}${Math.abs(move).toFixed(2)}%`,
      }
    },

    entryColor() {
      return this.isLong ? COLORS.long : COLORS.short
    },

    priorColor() {
      return this.isLong ? COLORS.priorLong : COLORS.priorShort
    },

    /**
     * Left-hand tags for the prior entry lines, stacked so none is lost.
     *
     * The library's own price-line labels are not usable here: it silently
     * drops any label that would overlap another, so two entries a few
     * pixels apart flicker in and out as the chart is zoomed or panned.
     * Drawing them here means every entry always has a tag, and the ones
     * that would collide get nudged apart instead of disappearing.
     */
    priorLabels() {
      const rows = []

      ;(this.field.prior_entries || []).forEach((prior, index) => {
        const top = this.priorY[index]
        if (top === null || top === undefined) return

        const price = Number(prior.price)
        const repeats = Number(prior.repeats) || 1
        const parts = [prior.jalali, this.formatPrice(price)].filter(Boolean)
        if (repeats > 1) parts.push(`×${repeats}`)

        rows.push({ key: index, top, text: parts.join(' · ') })
      })

      rows.sort((a, b) => a.top - b.top)

      for (let i = 1; i < rows.length; i += 1) {
        const overlap = rows[i - 1].top + LABEL_GAP - rows[i].top
        if (overlap > 0) rows[i].top += overlap
      }

      return rows
    },

    /**
     * The measure box + its read-out: price move, percent, leverage-adjusted
     * ROE, and elapsed time between the two clicked points. Positioned from
     * the per-frame pixel projection of the two logical anchors.
     */
    rulerBox() {
      const a = this.rulerAnchor
      const b = this.rulerEnd
      const pa = this.rulerAnchorPx
      const pb = this.rulerEndPx
      if (!a || !b || !pa || !pb) return null

      const left = Math.min(pa.x, pb.x)
      const top = Math.min(pa.y, pb.y)
      const width = Math.max(1, Math.abs(pb.x - pa.x))
      const height = Math.max(1, Math.abs(pb.y - pa.y))

      const start = Number(a.price)
      const end = Number(b.price)
      const move = end - start
      const pct = start > 0 ? (move / start) * 100 : 0
      const up = move >= 0
      const roe = this.leverage > 0 ? pct * this.leverage : null

      const seconds = Math.abs(Number(b.time) - Number(a.time))

      return {
        left,
        top,
        width,
        height,
        up,
        labelLeft: Math.min(left + width + 8, (this.$refs.wrapper?.clientWidth || 800) - 150),
        labelTop: Math.max(4, top),
        moveLabel: `${up ? '+' : '-'}${this.formatPrice(Math.abs(move))}`,
        pctLabel: `${up ? '+' : '-'}${Math.abs(pct).toFixed(2)}%`,
        roeLabel: roe === null ? '' : `${roe >= 0 ? '+' : '-'}${Math.abs(roe).toFixed(roe >= 100 ? 0 : 2)}%`,
        priceLabel: `${this.formatPrice(start)} → ${this.formatPrice(end)}`,
        timeLabel: this.formatDuration(seconds),
      }
    },
  },

  mounted() {
    this.initChart()
    this.loadCandles()
    if (this.field.live) {
      this.pollTimer = setInterval(() => this.loadCandles({ silent: true }), this.field.refresh_ms || 60000)
      this.onLiveUpdate = this.onLivePrices.bind(this)
      if (typeof Nova !== 'undefined' && Nova.$on) {
        Nova.$on('nova-live', this.onLiveUpdate)
      }
    }
    window.addEventListener('resize', this.onResize)
    this.trackLevels()
  },

  beforeUnmount() {
    clearInterval(this.pollTimer)
    cancelAnimationFrame(this.trackFrame)
    window.removeEventListener('resize', this.onResize)
    if (typeof Nova !== 'undefined' && Nova.$off && this.onLiveUpdate) {
      Nova.$off('nova-live', this.onLiveUpdate)
    }
    this.destroyChart()
  },

  watch: {
    interval() {
      this.clearRuler()
      this.loadCandles()
    },
  },

  methods: {
    apiBase() {
      return (this.field.api_base || '/api/desk').replace(/\/$/, '')
    },

    /**
     * Round a level to a readable number of decimals for its magnitude.
     *
     * @param  {number} price
     * @return {string}
     */
    formatPrice(price) {
      if (!Number.isFinite(price)) return ''

      const decimals = Number.isInteger(this.field.price_decimals)
        ? this.field.price_decimals
        : (price >= 1000 ? 1 : 3)

      return Number(price)
        .toFixed(decimals)
        .replace(/(\.\d*?)0+$/, '$1')
        .replace(/\.$/, '')
    },

    onLivePrices(data) {
      if (!this.field.live) return
      if (data?.action !== 'prices') return

      const coinId = this.field.coin_id
      if (!coinId) return

      const tick = (data.payload?.prices || {})[String(coinId)]
      const live = Number(tick?.last_price)
      if (live > 0) {
        this.lastClose = live
      }
    },

    initChart() {
      if (!this.$refs.container) return

      const clock = tehranChartClockOptions()

      this.chart = createChart(this.$refs.container, {
        width: this.$refs.wrapper?.clientWidth || 800,
        height: this.height,
        layout: {
          background: { type: ColorType.Solid, color: '#09090b' },
          textColor: '#a1a1aa',
        },
        grid: {
          vertLines: { color: 'rgba(63,63,70,0.45)' },
          horzLines: { color: 'rgba(63,63,70,0.45)' },
        },
        rightPriceScale: { borderColor: 'rgba(63,63,70,0.6)' },
        localization: clock.localization,
        timeScale: {
          ...clock.timeScale,
          borderColor: 'rgba(63,63,70,0.6)',
          timeVisible: true,
          secondsVisible: false,
        },
        crosshair: { mode: 0 },
      })

      this.series = this.chart.addCandlestickSeries({
        upColor: '#22c55e',
        downColor: '#ef4444',
        borderUpColor: '#22c55e',
        borderDownColor: '#ef4444',
        wickUpColor: '#22c55e',
        wickDownColor: '#ef4444',
      })

      this.priceLines = []

      this.chart.subscribeClick(this.onChartClick)
      this.chart.subscribeCrosshairMove(this.onCrosshairMove)
    },

    /**
     * Toggle the measure tool; leaving it clears any pending measurement.
     */
    toggleRuler() {
      this.rulerActive = !this.rulerActive
      if (!this.rulerActive) this.clearRuler()
    },

    clearRuler() {
      this.rulerAnchor = null
      this.rulerEnd = null
      this.rulerLocked = false
      this.rulerAnchorPx = null
      this.rulerEndPx = null
    },

    /**
     * Resolve a lightweight-charts event param to a logical {price, time}.
     *
     * @param  {object} param
     * @return {{price:number,time:number}|null}
     */
    pointFromParam(param) {
      if (!param || !param.point || !this.series) return null

      const price = this.series.coordinateToPrice(param.point.y)
      let time = param.time
      if (time == null) {
        time = this.chart.timeScale().coordinateToTime(param.point.x)
      }
      if (!Number.isFinite(Number(price)) || !Number.isFinite(Number(time))) return null

      return { price: Number(price), time: Number(time) }
    },

    onChartClick(param) {
      if (!this.rulerActive) return

      const p = this.pointFromParam(param)
      if (!p) return

      if (!this.rulerAnchor || this.rulerLocked) {
        // Start (or restart) a measurement.
        this.rulerAnchor = p
        this.rulerEnd = p
        this.rulerLocked = false
      } else {
        // Second click freezes the current end.
        this.rulerEnd = p
        this.rulerLocked = true
      }
    },

    onCrosshairMove(param) {
      if (!this.rulerActive || !this.rulerAnchor || this.rulerLocked) return

      const p = this.pointFromParam(param)
      if (p) this.rulerEnd = p
    },

    /**
     * Human elapsed time between two candle timestamps.
     *
     * @param  {number} seconds
     * @return {string}
     */
    formatDuration(seconds) {
      const s = Math.round(Math.abs(Number(seconds) || 0))
      if (s < 60) return `${s}s`
      const m = Math.round(s / 60)
      if (m < 60) return `${m}m`
      const h = Math.floor(m / 60)
      const rem = m % 60
      if (h < 24) return rem ? `${h}h ${rem}m` : `${h}h`
      const d = Math.floor(h / 24)
      const hr = h % 24
      return hr ? `${d}d ${hr}h` : `${d}d`
    },

    /**
     * Pixel position of one price, or null when it is off the pane.
     *
     * @param  {number} price
     * @return {number|null}
     */
    coordinateOf(price) {
      if (!this.series || !(price > 0)) return null

      const y = this.series.priceToCoordinate(price)
      if (!Number.isFinite(y) || y < 0 || y > this.height) return null

      return Math.round(y)
    },

    /**
     * Re-pin every floating label to its price line, once per frame.
     *
     * These labels are plain DOM over the canvas, so their pixel offsets
     * have to be recomputed whenever the price scale moves. Chart events are
     * not enough: an autoscaled scale also shifts on a plain time-axis zoom
     * (different candles in view, different range) and on a vertical drag of
     * the price axis, and the range is recalculated after those callbacks
     * fire — so a listener always lands a frame behind. Reading coordinates
     * per frame is a handful of multiplications, and the assignments only
     * re-render when a value actually changed.
     *
     * @return void
     */
    trackLevels() {
      const entry = this.coordinateOf(Number(this.field.entry))
      if (entry !== this.entryY) {
        this.entryY = entry
      }

      const priors = (this.field.prior_entries || []).map((prior) =>
        this.coordinateOf(Number(prior.price)),
      )
      if (priors.some((value, index) => value !== this.priorY[index]) || priors.length !== this.priorY.length) {
        this.priorY = priors
      }

      this.projectRuler()

      this.trackFrame = requestAnimationFrame(this.trackLevels)
    },

    /**
     * Re-project the two measure anchors (logical price/time) to pixel
     * coordinates so the box tracks pan / zoom / price-scale moves.
     */
    projectRuler() {
      if (!this.rulerAnchor || !this.rulerEnd || !this.series || !this.chart) {
        if (this.rulerAnchorPx || this.rulerEndPx) {
          this.rulerAnchorPx = null
          this.rulerEndPx = null
        }
        return
      }

      const ts = this.chart.timeScale()
      const project = (pt) => {
        const x = ts.timeToCoordinate(pt.time)
        const y = this.series.priceToCoordinate(pt.price)
        return Number.isFinite(x) && Number.isFinite(y) ? { x: Math.round(x), y: Math.round(y) } : null
      }

      const a = project(this.rulerAnchor)
      const b = project(this.rulerEnd)

      if (!a || !b) {
        if (this.rulerAnchorPx || this.rulerEndPx) {
          this.rulerAnchorPx = null
          this.rulerEndPx = null
        }
        return
      }

      if (!this.rulerAnchorPx || a.x !== this.rulerAnchorPx.x || a.y !== this.rulerAnchorPx.y) {
        this.rulerAnchorPx = a
      }
      if (!this.rulerEndPx || b.x !== this.rulerEndPx.x || b.y !== this.rulerEndPx.y) {
        this.rulerEndPx = b
      }
    },

    destroyChart() {
      if (this.chart) {
        this.chart.remove()
        this.chart = null
        this.series = null
        this.priceLines = []
      }
    },

    clearPriceLines() {
      for (const line of this.priceLines || []) {
        this.series?.removePriceLine(line)
      }
      this.priceLines = []
    },

    applyLevels() {
      if (!this.series) return
      this.clearPriceLines()

      const entry = Number(this.field.entry)
      const tp = Number(this.field.take_profit)
      const sl = Number(this.field.stop_loss)
      const priors = this.field.prior_entries || []

      // Line only — the tag that names it is drawn as DOM on the left.
      for (const prior of priors) {
        const price = Number(prior.price)
        if (!(price > 0)) continue
        this.priceLines.push(
          this.series.createPriceLine({
            price,
            color: this.priorColor,
            lineWidth: 1,
            lineStyle: LineStyle.Dashed,
            axisLabelVisible: false,
          }),
        )
      }

      if (tp > 0) {
        this.priceLines.push(
          this.series.createPriceLine({
            price: tp,
            color: COLORS.tp,
            lineWidth: 2,
            lineStyle: LineStyle.Solid,
            axisLabelVisible: true,
            title: `TP ${tp}`,
          }),
        )
      }

      if (sl > 0) {
        this.priceLines.push(
          this.series.createPriceLine({
            price: sl,
            color: COLORS.sl,
            lineWidth: 2,
            lineStyle: LineStyle.Solid,
            axisLabelVisible: true,
            title: `SL ${sl}`,
          }),
        )
      }

      if (entry > 0) {
        this.priceLines.push(
          this.series.createPriceLine({
            price: entry,
            color: this.entryColor,
            lineWidth: 3,
            lineStyle: LineStyle.Solid,
            axisLabelVisible: true,
            title: `ENTRY ${entry}`,
          }),
        )
      }
    },

    async loadCandles(options = {}) {
      if (!this.series || !this.chart) return
      if (!this.field.symbol) {
        this.candleError = 'Missing symbol'
        return
      }

      const silent = options.silent === true
      const seq = ++this._candleSeq || (this._candleSeq = 1)
      this._candleSeq = seq

      if (!silent) this.candleLoading = true
      this.candleError = ''

      try {
        const candles = this.field.live
          ? await this.fetchCandleWindow(null)
          : await this.fetchHistoryCandles()
        if (seq !== this._candleSeq) return

        if (!candles.length) throw new Error('No candles returned')

        this.series.setData(candles)
        this.lastClose = candles[candles.length - 1].close
        this.applyLevels()
        this.applyEventMarkers(candles)
        if (!silent) {
          this.chart.timeScale().fitContent()
          this.chart.timeScale().applyOptions({ rightOffset: 12 })
        }
      } catch (error) {
        if (seq !== this._candleSeq) return
        if (!silent) {
          this.candleError = error?.message || 'Failed to load market candles'
          this.series?.setData([])
          this.clearPriceLines()
          this.lastClose = 0
        }
      } finally {
        if (seq === this._candleSeq && !silent) {
          this.candleLoading = false
        }
      }
    },

    onResize() {
      if (!this.chart || !this.$refs.wrapper) return
      this.chart.applyOptions({ width: this.$refs.wrapper.clientWidth })
    },

    intervalSeconds() {
      return { '1m': 60, '5m': 300, '15m': 900, '1h': 3600, '4h': 14400 }[this.interval] || 60
    },

    historyRange() {
      const seconds = this.intervalSeconds()
      const pad = Math.max(7200, 200 * seconds)
      const opened = Number(this.field.opened_at) || 0
      const closed = Number(this.field.closed_at) || 0
      const created = Number(this.field.created_at) || 0
      const startAnchor = opened || created
      const endAnchor = closed || opened || created || Math.floor(Date.now() / 1000)

      return {
        start: Math.max(0, startAnchor - pad),
        end: endAnchor + pad,
      }
    },

    historyWindows() {
      const seconds = this.intervalSeconds()
      const pad = Math.max(7200, 180 * seconds)
      const maxSpan = 900 * seconds
      const opened = Number(this.field.opened_at) || 0
      const closed = Number(this.field.closed_at) || 0
      const created = Number(this.field.created_at) || 0
      const openAnchor = opened || created
      const around = (at) => ({
        start: Math.max(0, at - pad),
        end: at + pad,
      })

      if (openAnchor && closed && Math.abs(closed - openAnchor) > maxSpan) {
        return [around(openAnchor), around(closed)]
      }

      return [this.historyRange()]
    },

    async fetchHistoryCandles() {
      const seen = new Map()
      for (const range of this.historyWindows()) {
        const part = await this.fetchCandleWindow(range)
        for (const candle of part) {
          seen.set(candle.time, candle)
        }
      }

      return Array.from(seen.values()).sort((a, b) => a.time - b.time)
    },

    async fetchCandleWindow(range) {
      const exchange = this.field.exchange || 'binance'
      let url = `${this.apiBase()}/market/${exchange}/${this.field.symbol}/candles?interval=${this.interval}&limit=1000`
      if (range) {
        url += `&start=${range.start}&end=${range.end}`
      }
      const res = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.message || `HTTP ${res.status}`)
      }

      const payload = await res.json()

      return (payload.candles || []).map((c) => ({
        time: c.t,
        open: c.o,
        high: c.h,
        low: c.l,
        close: c.c,
      }))
    },

    snapCandleTime(unix, candles) {
      const at = Number(unix)
      if (!at || !candles.length) return null

      let best = candles[0].time
      for (let i = 0; i < candles.length; i += 1) {
        if (candles[i].time <= at) best = candles[i].time
        else break
      }

      return best
    },

    applyEventMarkers(candles) {
      if (!this.series || typeof this.series.setMarkers !== 'function') return

      const labels = this.field.labels || {}
      const markers = []
      const opened = this.snapCandleTime(this.field.opened_at, candles)
      const closed = this.snapCandleTime(this.field.closed_at, candles)

      if (opened) {
        markers.push({
          time: opened,
          position: 'belowBar',
          color: this.entryColor,
          shape: 'arrowUp',
          text: labels.opened || 'Open',
        })
      }

      if (closed) {
        const kind = this.field.close_kind
        const isTp = kind === 'tp'
        markers.push({
          time: closed,
          position: 'aboveBar',
          color: isTp ? COLORS.tp : '#ef4444',
          shape: 'arrowDown',
          text: isTp
            ? (labels.take_profit || 'TP')
            : (kind === 'cancelled' ? (labels.cancelled || 'Cancel') : (labels.stop_loss || 'SL')),
        })
      }

      this.series.setMarkers(markers)
    },
  },
}
</script>

<style scoped>
.mp-chart {
  width: 100%;
  min-width: 0;
}

.mp-chart__toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.mp-chart__legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.75rem;
  color: #a1a1aa;
}

.mp-chart__swatch {
  display: inline-block;
  width: 0.85rem;
  height: 0.2rem;
  border-radius: 999px;
  margin-inline-end: 0.2rem;
  vertical-align: middle;
}

.mp-chart__swatch--entry {
  height: 0.28rem;
}

.mp-chart__swatch--tp {
  background: #3b82f6;
}

.mp-chart__swatch--sl {
  background: #d4d4d8;
  box-shadow: none;
}

.mp-chart__intervals {
  display: flex;
  gap: 0.35rem;
}

.mp-chart__tf {
  border: 1px solid rgba(63, 63, 70, 0.9);
  background: #18181b;
  color: #a1a1aa;
  border-radius: 0.375rem;
  padding: 0.2rem 0.45rem;
  font-size: 0.7rem;
  line-height: 1.2;
  cursor: pointer;
}

.mp-chart__tf--active {
  background: #27272a;
  color: #fafafa;
  border-color: #52525b;
}

.mp-chart__tf--ruler {
  margin-inline-end: 0.35rem;
}

.mp-chart__stack {
  position: relative;
  width: 100%;
  border-radius: 0.5rem;
  overflow: hidden;
  border: 1px solid rgba(63, 63, 70, 0.8);
  background: #09090b;
}

.mp-chart__pane {
  width: 100%;
  height: 100%;
}

.mp-chart__stack--ruler .mp-chart__pane {
  cursor: crosshair;
}

.mp-chart__ruler-box {
  position: absolute;
  z-index: 3;
  pointer-events: none;
  border: 1px solid;
  background: rgba(59, 130, 246, 0.1);
}

.mp-chart__ruler-box--up {
  border-color: rgba(34, 197, 94, 0.9);
  background: rgba(34, 197, 94, 0.12);
}

.mp-chart__ruler-box--down {
  border-color: rgba(239, 68, 68, 0.9);
  background: rgba(239, 68, 68, 0.12);
}

.mp-chart__ruler-label {
  position: absolute;
  z-index: 4;
  pointer-events: none;
  direction: ltr;
  display: flex;
  flex-direction: column;
  gap: 0.05rem;
  padding: 0.25rem 0.4rem;
  border-radius: 0.3rem;
  border: 1px solid;
  background: rgba(9, 9, 11, 0.9);
  color: #fafafa;
  font-size: 0.65rem;
  line-height: 1.25;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.mp-chart__ruler-label--up {
  border-color: rgba(34, 197, 94, 0.9);
}

.mp-chart__ruler-label--down {
  border-color: rgba(239, 68, 68, 0.9);
}

.mp-chart__status {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(9, 9, 11, 0.72);
  color: #d4d4d8;
  font-size: 0.85rem;
}

.mp-chart__status--error {
  color: #fca5a5;
}

.mp-chart__prior {
  position: absolute;
  left: 0;
  transform: translateY(-50%);
  z-index: 3;
  direction: ltr;
  padding: 0.1rem 0.35rem;
  border-inline-start: 2px solid;
  background: rgba(9, 9, 11, 0.82);
  color: #d4d4d8;
  font-size: 0.65rem;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  pointer-events: none;
}

.mp-chart__pnl {
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;
  /* Nova runs the panel RTL, which would flip the chips and push the +/− sign
     behind its number. Numbers and this badge's reading order are LTR. */
  direction: ltr;
  display: flex;
  align-items: stretch;
  overflow: hidden;
  border-radius: 0.3rem;
  border: 1px solid;
  font-size: 0.7rem;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  pointer-events: none;
}

.mp-chart__pnl-lev,
.mp-chart__pnl-roe,
.mp-chart__pnl-move {
  padding: 0.2rem 0.4rem;
}

.mp-chart__pnl-lev {
  background: #18181b;
  color: #d4d4d8;
  font-weight: 600;
}

.mp-chart__pnl-roe {
  color: #09090b;
  font-weight: 700;
}

.mp-chart__pnl-move {
  color: #fafafa;
  background: rgba(9, 9, 11, 0.85);
}

.mp-chart__pnl--up {
  border-color: #22c55e;
}

.mp-chart__pnl--up .mp-chart__pnl-roe {
  background: #22c55e;
}

.mp-chart__pnl--down {
  border-color: #ef4444;
}

.mp-chart__pnl--down .mp-chart__pnl-roe {
  background: #ef4444;
}
</style>
